import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import type { EntityRow } from './types';

interface DivergingChartProps {
  data: EntityRow[];
  metric: 'passPercentage' | 'performanceIndex';
  title: string;
  posColor: string;
  negColor: string;
  onBarClick?: (name: string) => void;
}

const DivergingChart: React.FC<DivergingChartProps> = function ({ data, metric, title, posColor, negColor, onBarClick }) {
  const avg = data.length
    ? +(data.reduce(function (s, d) { return s + d[metric]; }, 0) / data.length).toFixed(2)
    : 0;
  const chartData = [...data]
    .sort(function (a, b) { return b[metric] - a[metric]; })
    .map(function (d) {
      return { name: d.name, value: +(d[metric] - avg).toFixed(2), actual: d[metric] };
    });

  if (!chartData.length) {
    return (
      <div className="bg-white rounded-xl border p-6 text-center text-gray-400 text-sm h-40 flex items-center justify-center">
        No data
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-1 gap-3">
        <h3 className="text-base font-semibold text-gray-800">{title}</h3>
        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">
          {metric === 'performanceIndex' ? 'PI' : 'Pass%'}: {avg}
        </span>
      </div>
      <p className="text-xs text-gray-400 mb-4">
        Deviation from average{onBarClick ? ' — click to drill down' : ''}
      </p>
      <div className="overflow-x-auto pb-2">
        <div style={{ minWidth: Math.max(500, chartData.length * 64) }}>
          <ResponsiveContainer width="100%" height={420}>
            <BarChart
              data={chartData}
              margin={{ top: 24, right: 12, left: 8, bottom: 80 }}
              onClick={function (e: any) {
                if (e && e.activeLabel && onBarClick) onBarClick(e.activeLabel);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
                interval={0}
                height={90}
                angle={-35}
                textAnchor="end"
                tick={function (props: any) {
                  const { x, y, payload } = props;
                  const words = String(payload.value).split(' ');
                  const mid = Math.ceil(words.length / 2);
                  const line1 = words.slice(0, mid).join(' ');
                  const line2 = words.slice(mid).join(' ');
                  return (
                    <g transform={`translate(${x},${y}) rotate(-35)`}>
                      <text x={0} y={0} textAnchor="end" fill="#667085" fontSize={10}>
                        <tspan x={0} dy={4}>{line1}</tspan>
                        {line2 && <tspan x={0} dy={13}>{line2}</tspan>}
                      </text>
                    </g>
                  );
                }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#667085' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={function (v: number) { return (v + avg).toFixed(1) + '%'; }}
              />
              <ReferenceLine y={0} stroke="#374151" strokeWidth={1.5} />
              <Tooltip
                content={function (props: any) {
                  const { active, payload, label } = props;
                  if (!active || !payload || !payload.length) return null;
                  const p = payload[0].payload;
                  return (
                    <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg text-sm">
                      <p className="font-semibold text-gray-800 mb-1">{label}</p>
                      <p className="text-gray-600">Actual: <span className="font-bold">{p.actual}%</span></p>
                      <p style={{ color: p.value >= 0 ? posColor : negColor }}>
                        Dev: <span className="font-bold">{p.value >= 0 ? '+' : ''}{p.value}%</span>
                      </p>
                    </div>
                  );
                }}
              />
              <Bar
                dataKey="value"
                radius={[3, 3, 0, 0]}
                maxBarSize={36}
                style={{ cursor: onBarClick ? 'pointer' : 'default' }}
              >
                {chartData.map(function (e, i) {
                  return <Cell key={i} fill={e.value >= 0 ? posColor : negColor} fillOpacity={0.85} />;
                })}
                <LabelList
                  dataKey="actual"
                  position="top"
                  formatter={function (v: any) { return v + '%'; }}
                  style={{ fontSize: 9, fill: '#374151' }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default DivergingChart;
